import { ExportButton } from "@components/exports/ExportButton";
import { HelpLink } from "@components/help/HelpLink";
import {
  EXPORT_ASSESSMENT_MATRIX_TO_GOOGLE_DOCS,
  EXPORT_ASSESSMENT_MATRIX_TO_GOOGLE_SHEETS,
  GENERATE_ASSESSMENT_MATRIX,
  TOGGLE_ASSESSMENT_MATRIX_LOCK,
  UPDATE_ASSESSMENT_MATRIX_BAND,
} from "@lib/graphql/mutations";
import { ASSESSMENT_MATRIX_PAGE_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  ClipboardCopy,
  Edit3,
  Loader2,
  Lock,
  RefreshCw,
  Sparkles,
  Unlock,
  X,
} from "lucide-react";
import { useCallback, useState } from "react";
import { Link, useParams } from "react-router";
import { useMutation, useQuery } from "urql";
import { Button } from "../shared/Button";
import { Card } from "../shared/Card";
import { RegenerateConfirmDialog } from "../shared/RegenerateConfirmDialog";
import { StateBadge } from "../shared/StateBadge";
import { MatrixViewToggle } from "./MatrixViewToggle";
import { StalenessBanner } from "./StalenessBanner";
import { type AssessmentGuideData, AssessmentGuidePanel } from "./stages/AssessmentGuidePanel";

const LEVEL_ORDER = ["high", "mid", "low"] as const;
type Level = (typeof LEVEL_ORDER)[number];

interface BandData {
  id: string;
  capabilityDimensionId: string;
  level: string;
  levelLabel: string;
  body: string;
  position: number;
  effectiveFrom: string;
}

interface DimensionData {
  id: string;
  name: string;
  idealScore: number | null;
  critical: boolean;
}

interface VersionData {
  id: string;
  versionNumber: number;
  generationState: string;
  generationError: string | null;
  roleBlueprintVersionId: string | null;
  roleBlueprintVersionNumber: number | null;
  effectiveFrom: string;
  model: string | null;
}

interface MatrixData {
  id: string;
  locked: boolean;
  state: string;
  outOfSync: boolean;
  currentVersion: VersionData | null;
  dimensions: DimensionData[];
  bands: BandData[];
}

interface CampaignData {
  id: string;
  title: string;
  roleBlueprint: { id: string; locked: boolean } | null;
  assessmentGuide: AssessmentGuideData | null;
  assessmentMatrix: MatrixData;
}

interface AssessmentMatrixPageProps {
  // When true the matrix renders inside the DEFINE › Assessment slot (#2217),
  // so the standalone-page chrome — outer padding, back-link, scores toggle,
  // and the campaign-title heading — is dropped to avoid doubling up with the
  // campaign header and sub-nav already on screen.
  embedded?: boolean;
}

export function AssessmentMatrixPage({ embedded = false }: AssessmentMatrixPageProps = {}) {
  const { id } = useParams();
  const campaignId = id ?? "";

  const [{ data, fetching, error }, reexecute] = useQuery<{ campaign: CampaignData }>({
    query: ASSESSMENT_MATRIX_PAGE_QUERY,
    variables: { id: campaignId },
  });
  const refresh = useCallback(() => reexecute({ requestPolicy: "network-only" }), [reexecute]);
  useCampaignChannel(campaignId, refresh);

  const [{ fetching: generating }, generate] = useMutation(GENERATE_ASSESSMENT_MATRIX);
  const [{ fetching: toggling }, toggleLock] = useMutation(TOGGLE_ASSESSMENT_MATRIX_LOCK);
  const [, updateBand] = useMutation(UPDATE_ASSESSMENT_MATRIX_BAND);
  const [, exportToDocs] = useMutation(EXPORT_ASSESSMENT_MATRIX_TO_GOOGLE_DOCS);
  const [, exportToSheets] = useMutation(EXPORT_ASSESSMENT_MATRIX_TO_GOOGLE_SHEETS);

  const [editing, setEditing] = useState<{ dimId: string; level: Level } | null>(null);
  const [draftBody, setDraftBody] = useState("");
  const [savingBand, setSavingBand] = useState(false);
  const [bannerError, setBannerError] = useState<string | null>(null);
  // Confirmation payload when regenerate hits manual_edits_present.
  const [regenerateConfirm, setRegenerateConfirm] = useState<{
    warning: string;
    deepLinkPath?: string | null;
    deepLinkLabel?: string | null;
  } | null>(null);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");

  const campaign = data?.campaign;
  const matrix = campaign?.assessmentMatrix;
  const blueprintLocked = campaign?.roleBlueprint?.locked ?? false;
  const isGenerating = matrix?.currentVersion?.generationState === "generating";
  const nextVersionNumber = (matrix?.currentVersion?.versionNumber ?? 0) + 1;
  const generationError =
    matrix?.currentVersion?.generationState === "error"
      ? matrix.currentVersion.generationError
      : null;

  const bandsByDim = new Map<string, Partial<Record<Level, BandData>>>();
  matrix?.bands.forEach((b) => {
    const lvl = b.level as Level;
    if (!LEVEL_ORDER.includes(lvl)) return;
    const slot = bandsByDim.get(b.capabilityDimensionId) ?? {};
    slot[lvl] = b;
    bandsByDim.set(b.capabilityDimensionId, slot);
  });

  const runGenerate = async (force: boolean, confirmOverwrite: boolean) => {
    if (!blueprintLocked) {
      setBannerError("Lock the role blueprint before generating the assessment matrix.");
      return;
    }
    setBannerError(null);
    const res = await generate({
      input: { campaignId, force, confirmOverwrite },
    });
    const data = res.data?.generateAssessmentMatrix;
    const errs = data?.errors ?? [];
    if (errs.length) {
      setBannerError(errs.join(", "));
      return;
    }
    if (data?.status === "manual_edits_present" && !confirmOverwrite) {
      setRegenerateConfirm({
        warning: data.warning ?? "Manual edits will be replaced.",
        deepLinkPath: data.deepLinkPath ?? null,
        deepLinkLabel: data.deepLinkLabel ?? null,
      });
      return;
    }
    refresh();
  };

  const handleGenerate = (force: boolean) => runGenerate(force, false);

  const handleToggleLock = async () => {
    setBannerError(null);
    await toggleLock({ input: { campaignId } });
    refresh();
  };

  const buildClipboardTable = (): { html: string; plain: string } | null => {
    if (!matrix || !campaign) return null;
    const escHtml = (s: string) =>
      s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    const cellHtml = (body: string) => escHtml(body).replace(/\r?\n/g, "<br>");
    const headers = ["Dimension", "High (4–5)", "Mid (3)", "Low (1–2)"];

    const headerRow = `<tr>${headers.map((h) => `<th>${escHtml(h)}</th>`).join("")}</tr>`;
    const bodyRows = matrix.dimensions
      .map((dim) => {
        const bands = bandsByDim.get(dim.id) ?? {};
        const cells = [
          escHtml(dim.name),
          cellHtml(bands.high?.body ?? ""),
          cellHtml(bands.mid?.body ?? ""),
          cellHtml(bands.low?.body ?? ""),
        ];
        return `<tr>${cells.map((c) => `<td>${c}</td>`).join("")}</tr>`;
      })
      .join("");
    const html = `<table border="1" cellspacing="0" cellpadding="6"><thead>${headerRow}</thead><tbody>${bodyRows}</tbody></table>`;

    const plainHeader = headers.join("\t");
    const plainBody = matrix.dimensions
      .map((dim) => {
        const bands = bandsByDim.get(dim.id) ?? {};
        return [dim.name, bands.high?.body ?? "", bands.mid?.body ?? "", bands.low?.body ?? ""]
          .map((c) => c.replace(/\t/g, " ").replace(/\r?\n/g, " "))
          .join("\t");
      })
      .join("\n");
    const plain = `${plainHeader}\n${plainBody}`;

    return { html, plain };
  };

  const handleCopy = async () => {
    const built = buildClipboardTable();
    if (!built) return;
    try {
      if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([built.html], { type: "text/html" }),
            "text/plain": new Blob([built.plain], { type: "text/plain" }),
          }),
        ]);
      } else {
        await navigator.clipboard.writeText(built.plain);
      }
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 2000);
    } catch {
      setCopyState("failed");
      setTimeout(() => setCopyState("idle"), 2000);
    }
  };

  const handleExportToDocsStart = async () => {
    const result = await exportToDocs({ input: { campaignId } });
    const payload = result.data?.exportAssessmentMatrixToGoogleDocs;
    return {
      documentExportId: payload?.documentExport?.id ?? null,
      errors: payload?.errors ?? [],
    };
  };

  const handleExportToSheetsStart = async () => {
    const result = await exportToSheets({ input: { campaignId } });
    const payload = result.data?.exportAssessmentMatrixToGoogleSheets;
    return {
      documentExportId: payload?.documentExport?.id ?? null,
      errors: payload?.errors ?? [],
    };
  };

  const hasBands = (matrix?.bands?.length ?? 0) > 0;

  const startEditing = (dimId: string, level: Level, body: string) => {
    if (matrix?.locked) return;
    setEditing({ dimId, level });
    setDraftBody(body);
  };

  const cancelEditing = () => {
    setEditing(null);
    setDraftBody("");
  };

  const saveEditing = async () => {
    if (!editing) return;
    if (!draftBody.trim()) {
      setBannerError("Band text cannot be empty.");
      return;
    }
    setSavingBand(true);
    setBannerError(null);
    const res = await updateBand({
      input: {
        campaignId,
        capabilityDimensionId: editing.dimId,
        level: editing.level,
        body: draftBody,
      },
    });
    setSavingBand(false);
    const errs = res.data?.updateAssessmentMatrixBand?.errors ?? [];
    if (errs.length) {
      setBannerError(errs.join(", "));
      return;
    }
    setEditing(null);
    setDraftBody("");
    refresh();
  };

  const containerClass = embedded ? "space-y-4" : "space-y-4 p-6";
  const backLink = embedded ? null : (
    <Link
      to={`/campaigns/${campaignId}/run/pipeline/scoring`}
      className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary"
    >
      <ArrowLeft size={14} />
      Back to scoring
    </Link>
  );

  if (fetching && !campaign) {
    return (
      <div className={containerClass}>
        {backLink}
        <p className="text-sm text-text-muted">Loading assessment matrix…</p>
      </div>
    );
  }
  if (error || !campaign || !matrix) {
    return (
      <div className={containerClass}>
        {backLink}
        <p className="text-sm text-error">
          Could not load campaign. {error?.message ?? "No data returned."}
        </p>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      {!embedded && (
        <div className="flex items-center justify-between">
          {backLink}
          <MatrixViewToggle campaignId={campaignId} />
        </div>
      )}

      <StalenessBanner
        campaignId={campaignId}
        kinds={["assessment_matrix", "assessment_guide", "candidate_scoring_matrix"]}
      />

      {/* Story #2252: Assessment Guide sits at the top of the Assessment view,
          collapsed by default. It derives from the matrix bands, so the panel
          gates Generate on matrix presence. */}
      <AssessmentGuidePanel
        campaignId={campaignId}
        guide={campaign.assessmentGuide}
        matrixReady={matrix.currentVersion !== null}
        onSaved={refresh}
        defaultCollapsed
      />

      <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-lg font-semibold text-text-primary">
              {embedded ? "Assessment Matrix" : `Assessment Matrix — ${campaign.title}`}
            </h1>
            <p
              className="mt-1 flex items-center gap-1.5 text-xs text-text-faint"
              data-testid="rubric-version-meta"
            >
              <span>
                Per capability dimension, the criteria for each rating band (4–5, 3, 1–2).
              </span>
              <HelpLink to="/help/assessment-matrix" label="Guide: assessment matrix" />
            </p>
            <p className="mt-1 text-xs text-text-faint">
              {matrix.currentVersion ? (
                <>
                  v{matrix.currentVersion.versionNumber}
                  {matrix.currentVersion.roleBlueprintVersionNumber != null &&
                    ` · pinned to blueprint v${matrix.currentVersion.roleBlueprintVersionNumber}`}
                  {matrix.currentVersion.model && ` · ${matrix.currentVersion.model}`}
                </>
              ) : (
                "Not yet generated"
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span data-testid="matrix-state-badge">
              <StateBadge
                tone={matrix.locked ? "warning" : "muted"}
                label={matrix.locked ? "locked" : "draft"}
              />
            </span>
            <button
              type="button"
              // Always force=true on user-initiated clicks. The job's
              // force=false path is for the cascade-from-lock auto-trigger
              // (idempotent so token spend is bounded); a manual Regenerate
              // click on an already-pinned version would silently no-op.
              onClick={() => handleGenerate(matrix.currentVersion != null)}
              disabled={!blueprintLocked || isGenerating || generating || matrix.locked}
              title={
                !blueprintLocked
                  ? "Lock the role blueprint first"
                  : matrix.locked
                    ? "Unlock the assessment matrix to update it"
                    : isGenerating
                      ? hasBands
                        ? "Update already in progress"
                        : "Generation already in progress"
                      : hasBands
                        ? "Update the assessment matrix from the role blueprint"
                        : "Generate the assessment matrix from the role blueprint"
              }
              className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="generate-matrix-button"
            >
              {isGenerating || generating ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Sparkles size={14} />
              )}
              {hasBands ? "Update assessment matrix" : "Generate assessment matrix"}
            </button>
            <button
              type="button"
              onClick={handleToggleLock}
              disabled={toggling}
              className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-medium hover:bg-card-hover ${
                matrix.locked ? "text-highlight" : "text-text-secondary"
              }`}
            >
              {matrix.locked ? <Lock size={14} /> : <Unlock size={14} />}
              {matrix.locked ? "Unlock" : "Lock"}
            </button>
            {hasBands && (
              <>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover"
                  data-testid="copy-matrix-button"
                >
                  <ClipboardCopy size={14} />
                  {copyState === "copied"
                    ? "Copied"
                    : copyState === "failed"
                      ? "Copy failed"
                      : "Copy"}
                </button>
                <ExportButton
                  label="Export to Google Docs"
                  onStart={handleExportToDocsStart}
                  className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
                />
                <ExportButton
                  label="Export to Google Sheets"
                  onStart={handleExportToSheetsStart}
                  className="flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
                />
              </>
            )}
          </div>
        </div>

        {matrix.outOfSync && (
          <div
            className="mt-3 flex items-start justify-between gap-3 rounded border border-highlight/30 bg-highlight-bg p-3"
            data-testid="out-of-sync-banner"
          >
            <div className="flex items-start gap-2">
              <RefreshCw size={14} className="mt-0.5 text-highlight" />
              <div className="text-sm text-highlight">
                <p className="font-medium">Capability dimensions have changed.</p>
                <p className="mt-0.5 text-xs">
                  The assessment matrix is pinned to an earlier blueprint version. Update it to
                  match the current dimensions.
                </p>
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleGenerate(true)}
              disabled={generating || isGenerating || matrix.locked}
              data-testid="update-matrix-button"
            >
              Update the assessment matrix
            </Button>
          </div>
        )}

        {generationError && (
          <div className="mt-3 flex items-start gap-2 rounded border border-error/30 bg-error-bg p-2 text-sm text-error">
            <AlertTriangle size={14} className="mt-0.5" />
            <span>Generation failed: {generationError}</span>
          </div>
        )}

        {bannerError && (
          <div className="mt-3 flex items-start gap-2 rounded border border-error/30 bg-error-bg p-2 text-sm text-error">
            <AlertTriangle size={14} className="mt-0.5" />
            <span>{bannerError}</span>
          </div>
        )}
      </div>

      {matrix.dimensions.length === 0 ? (
        <div className="rounded-lg bg-card p-6 text-sm text-text-muted shadow-sm ring-1 ring-ring">
          No capability dimensions defined yet. Add some in the Role Blueprint stage to generate the
          assessment matrix.
        </div>
      ) : isGenerating && matrix.bands.length === 0 ? (
        <div className="flex items-center gap-2 rounded-lg bg-card p-6 text-sm text-text-muted shadow-sm ring-1 ring-ring">
          <Loader2 size={14} className="animate-spin" />
          Drafting the assessment matrix…
        </div>
      ) : matrix.bands.length === 0 ? (
        <div className="rounded-lg bg-card p-6 text-sm text-text-muted shadow-sm ring-1 ring-ring">
          The assessment matrix has not been generated yet. Click <strong>Generate</strong> to draft
          it from the locked role blueprint.
        </div>
      ) : (
        <>
          <ScoringKey />

          {/* Desktop: matrix table (rating bands × dimensions). Carries the
              primary testids since this is the canonical layout. */}
          <div
            className="hidden overflow-x-auto rounded-lg bg-card shadow-sm ring-1 ring-ring md:block"
            data-testid="matrix-grid"
          >
            <table className="min-w-full table-fixed border-collapse">
              <thead>
                <tr className="border-b border-border bg-card-alt">
                  <th className="w-24 p-3 text-left text-xs font-semibold text-text-faint">
                    Rating band
                  </th>
                  {matrix.dimensions.map((dim) => (
                    <th
                      key={dim.id}
                      scope="col"
                      className="border-l border-border p-3 align-top"
                      data-testid={`dimension-card-${dim.id}`}
                    >
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="text-sm font-semibold text-text-primary">{dim.name}</span>
                        {dim.idealScore != null && (
                          <span className="text-xs font-normal text-text-faint">
                            ideal {dim.idealScore}/10
                          </span>
                        )}
                        {dim.critical && (
                          <span className="rounded bg-error-bg px-1 text-[10px] font-medium uppercase tracking-wide text-error">
                            critical
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {LEVEL_ORDER.map((level) => (
                  <tr key={level} className="border-b border-border last:border-b-0 align-top">
                    <th scope="row" className="w-24 p-3 text-left align-top text-xs font-semibold">
                      <LevelBadge level={level} label={labelFor(level)} />
                    </th>
                    {matrix.dimensions.map((dim) => {
                      const band = bandsByDim.get(dim.id)?.[level];
                      const isEditing = editing?.dimId === dim.id && editing?.level === level;
                      return (
                        <td
                          key={dim.id}
                          className="border-l border-border p-3 align-top"
                          data-testid={`band-${dim.id}-${level}`}
                        >
                          <BandCell
                            dimId={dim.id}
                            level={level}
                            band={band}
                            isEditing={isEditing}
                            locked={matrix.locked}
                            draftBody={draftBody}
                            setDraftBody={setDraftBody}
                            savingBand={savingBand}
                            nextVersionNumber={nextVersionNumber}
                            onStartEditing={startEditing}
                            onSave={saveEditing}
                            onCancel={cancelEditing}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: per-dimension card stack (more ergonomic on narrow
              screens). Testids are prefixed `mobile-` so jsdom (which renders
              both layouts) doesn't see duplicates of the matrix testids. */}
          <div className="space-y-3 md:hidden" data-testid="matrix-stack">
            {matrix.dimensions.map((dim) => {
              const slots = bandsByDim.get(dim.id) ?? {};
              return (
                <Card
                  key={dim.id}
                  padding="none"
                  leftBorderTone={dim.critical ? "error" : "info"}
                  data-testid={`mobile-dimension-card-${dim.id}`}
                >
                  <div className="flex items-center justify-between gap-2 border-b border-border p-3">
                    <div className="flex items-center gap-2">
                      <h2 className="text-sm font-semibold text-text-primary">{dim.name}</h2>
                      {dim.idealScore != null && (
                        <span className="text-xs text-text-faint">ideal {dim.idealScore}/10</span>
                      )}
                      {dim.critical && (
                        <span className="rounded bg-rose-100 px-1 text-[10px] font-medium text-rose-700 dark:bg-rose-900/30 dark:text-rose-400">
                          critical
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="divide-y divide-border">
                    {LEVEL_ORDER.map((level) => {
                      const band = slots[level];
                      const isEditing = editing?.dimId === dim.id && editing?.level === level;
                      return (
                        <div
                          key={level}
                          className="flex gap-3 p-3"
                          data-testid={`mobile-band-${dim.id}-${level}`}
                        >
                          <div className="flex-shrink-0">
                            <LevelBadge level={level} label={band?.levelLabel ?? labelFor(level)} />
                          </div>
                          <div className="flex-1">
                            <BandCell
                              dimId={dim.id}
                              level={level}
                              band={band}
                              isEditing={isEditing}
                              locked={matrix.locked}
                              draftBody={draftBody}
                              setDraftBody={setDraftBody}
                              savingBand={savingBand}
                              nextVersionNumber={nextVersionNumber}
                              onStartEditing={startEditing}
                              onSave={saveEditing}
                              onCancel={cancelEditing}
                              testidPrefix="mobile-"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}
      <RegenerateConfirmDialog
        open={regenerateConfirm !== null}
        warning={regenerateConfirm?.warning ?? ""}
        deepLinkPath={regenerateConfirm?.deepLinkPath}
        deepLinkLabel={regenerateConfirm?.deepLinkLabel}
        onCancel={() => setRegenerateConfirm(null)}
        onConfirm={() => {
          setRegenerateConfirm(null);
          void runGenerate(true, true);
        }}
      />
    </div>
  );
}

function ScoringKey() {
  return (
    <div className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring" data-testid="scoring-key">
      <h2 className="mb-2 text-sm font-semibold text-text-primary">Scoring Key</h2>
      <ul className="space-y-0.5 text-xs text-text-secondary">
        <li>
          <strong className="font-semibold">1:</strong> Clear evidence of not meeting requirement
        </li>
        <li>
          <strong className="font-semibold">2:</strong> Partially meets; material gaps
        </li>
        <li>
          <strong className="font-semibold">3:</strong> Meets requirement; baseline hire standard
        </li>
        <li>
          <strong className="font-semibold">4:</strong> Strongly meets; above standard
        </li>
        <li>
          <strong className="font-semibold">5:</strong> Significantly exceeds; standout
        </li>
      </ul>
    </div>
  );
}

function LevelBadge({ level, label }: { level: Level; label: string }) {
  const cls =
    level === "high"
      ? "bg-success-bg text-success"
      : level === "mid"
        ? "bg-info-bg text-info"
        : "bg-warning-bg text-warning";
  return (
    <span
      className={`inline-flex h-6 w-12 items-center justify-center rounded text-xs font-semibold ${cls}`}
    >
      {label}
    </span>
  );
}

interface BandCellProps {
  dimId: string;
  level: Level;
  band: BandData | undefined;
  isEditing: boolean;
  locked: boolean;
  draftBody: string;
  setDraftBody: (s: string) => void;
  savingBand: boolean;
  nextVersionNumber: number;
  onStartEditing: (dimId: string, level: Level, body: string) => void;
  onSave: () => void;
  onCancel: () => void;
  testidPrefix?: string;
}

function BandCell({
  dimId,
  level,
  band,
  isEditing,
  locked,
  draftBody,
  setDraftBody,
  savingBand,
  nextVersionNumber,
  onStartEditing,
  onSave,
  onCancel,
  testidPrefix = "",
}: BandCellProps) {
  if (isEditing) {
    return (
      <div className="space-y-2">
        <textarea
          value={draftBody}
          onChange={(e) => setDraftBody(e.target.value)}
          className="w-full rounded border border-border bg-card-alt p-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent"
          rows={4}
          ref={(el) => el?.focus()}
          data-testid={`${testidPrefix}band-editor-${dimId}-${level}`}
        />
        <p
          className="text-xs text-text-faint"
          data-testid={`${testidPrefix}band-edit-version-hint-${dimId}-${level}`}
        >
          Saving will create v{nextVersionNumber} of the assessment matrix.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onSave}
            disabled={savingBand}
            className="inline-flex items-center gap-1 rounded bg-accent px-2 py-1 text-xs font-medium text-white hover:bg-accent-hover disabled:opacity-50"
            data-testid={`${testidPrefix}band-save-${dimId}-${level}`}
          >
            {savingBand ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            Save
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center gap-1 rounded border border-border px-2 py-1 text-xs font-medium text-text-secondary hover:bg-card-hover"
          >
            <X size={12} />
            Cancel
          </button>
        </div>
      </div>
    );
  }
  if (band) {
    return (
      <div className="group flex items-start justify-between gap-2">
        <p className="whitespace-pre-wrap text-sm text-text-primary">{band.body}</p>
        {!locked && (
          <button
            type="button"
            onClick={() => onStartEditing(dimId, level, band.body)}
            title="Edit band"
            className="flex-shrink-0 rounded p-1 text-text-faint opacity-0 hover:bg-card-hover group-hover:opacity-100"
            data-testid={`${testidPrefix}band-edit-${dimId}-${level}`}
          >
            <Edit3 size={12} />
          </button>
        )}
      </div>
    );
  }
  return <p className="text-sm italic text-text-faint">No criteria yet for this band.</p>;
}

function labelFor(level: Level): string {
  return level === "high" ? "4–5" : level === "mid" ? "3" : "1–2";
}
